import { showToast } from '../Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useBrand } from '../../contexts/BrandContext';
import { BrandName } from '../Common/BrandedText';
import AddCustomerModal from '../AddCustomerModal';
import {
  RocketLaunchIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  CreditCardIcon,
  SparklesIcon,
  ArrowRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

import stripeConnectService from '../../services/StripeConnectService';
import SmbMateProcessorNotice from '../Stripe/SmbMateProcessorNotice';
import { supabase } from '../../utils/supabaseClient';
const GoLiveStep = ({ onComplete, onValidationChange, registerSave }) => {
  const { user } = useUser();
  const { brand } = useBrand();
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerCreated, setCustomerCreated] = useState(null);
  // Getting paid is the point of the product, and a contractor who finishes setup could
  // not take a card: Stripe Connect existed but lived only in Settings, which a new user
  // has no reason to open yet. Every comparable product puts this in the setup flow.
  const [stripeConnected, setStripeConnected] = useState(null); // null = still checking
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  // Always valid - this is the final step
  useEffect(() => {
    onValidationChange?.({
      valid: true,
      errors: [],
      warnings: []
    });
  }, []);


  useEffect(() => {
    let cancelled = false;
    if (!user?.company_id) return undefined;
    supabase
      .from('companies')
      .select('stripe_account_id, stripe_onboarding_complete')
      .eq('id', user.company_id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setStripeConnected(!!data?.stripe_account_id && !!data?.stripe_onboarding_complete);
      });
    return () => { cancelled = true; };
  }, [user?.company_id]);

  // WHAT JUST HAPPENED AT STRIPE. Connect sends the user back with ?stripe=success or
  // ?stripe=refresh, and nothing here read either, so returning looked identical to
  // never having left: the same "Set Up Card Payments" button, no explanation. Clicking
  // it again produced a fresh link to the same unfinished account, which is the loop
  // reported live.
  //
  // refresh is not an error. Stripe uses refresh_url when the account link EXPIRED or the
  // account still has outstanding requirements -- account links are single-use and
  // short-lived -- and the documented response is to mint a new one, which is exactly
  // what Resume does. Saying so is the difference between a dead end and a next step.
  const [stripeReturn, setStripeReturn] = useState(null);
  useEffect(() => {
    const status = new URLSearchParams(window.location.search).get('stripe');
    if (status === 'success' || status === 'refresh') setStripeReturn(status);
  }, []);

  const connectStripe = async () => {
    try {
      setConnectingStripe(true);
      const result = await stripeConnectService.startStripeOnboarding();
      if (result?.data?.url) {
        // Stripe hosts the rest; it returns to the app via the service's configured URLs.
        window.location.href = result.data.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      showToast('error', err?.message || 'Could not start payment setup');
      setConnectingStripe(false);
    }
  };

  // Handle successful customer creation from AddCustomerModal.
  //
  // `setupComplete` now switches this screen to its welcome/success state. It no longer
  // gates the Launch button — that button is always enabled, because adding a first
  // customer is optional (the panel below says so) and the step reports valid: true.
  // The earlier note here said flipping this flag was needed so Launch would enable,
  // "otherwise the only way to advance is the Skip link"; that was true of the gated
  // version and is not true now. Updated rather than left, because a comment describing
  // behaviour that no longer exists is how the next reader inherits a wrong model.
  const handleCustomerCreated = (customer) => {
    setCustomerCreated(customer);
    setShowAddCustomerModal(false);
    setSetupComplete(true);
  };

  const skipCustomer = () => {
    setSetupComplete(true);
  };

  const completeOnboarding = () => {
    onComplete?.({ customerCreated, setupComplete: true });
  };

  // REGISTERED so the footer's "Complete Setup" runs THIS, not a bare
  // handleStepComplete(). The payload matters: handleStepComplete writes its argument
  // to step_6_data, and the footer calls it with none -- so simply deleting the old
  // in-form Launch button would have quietly stopped recording whether a first customer
  // was created. Same registration the other steps use.
  //
  // Left unmemoized on purpose, matching PricingSetupStep: a stable callback registers
  // once and the wizard's step-tagged ref may not have it for the step on screen.
  useEffect(() => { registerSave?.(completeOnboarding); }, [registerSave, completeOnboarding]);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <RocketLaunchIcon className="w-12 h-12 text-pink-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ready to Go Live!</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Add your first customer now so you can create a real quote right away. 
          Or skip to explore the dashboard first.
        </p>
      </div>

      {!setupComplete ? (
        <>
          {/* The "Setup Summary" that sat here was REMOVED 2026-09-08. It was six
              hardcoded green ticks -- Company profile, Pricing set up, Team members,
              Business settings, Financial settings -- with no data behind any of them.
              A tester clicked through Advanced Step 2 with no trade template and no custom
              service and was still told "Pricing set up".

              Worse on the Quick path, which is steps [1, 2, 6]: it asserted Team members,
              Business settings and Financial settings to a user who was never shown those
              steps at all.

              Same class as the Reports Compliance Checklist removed in 6797a8865, and the
              same ruling applies -- a fabricated green tick is worse than no summary,
              because it is the screen a contractor would point at to say they had set the
              product up. Deriving these six from real state is the better answer and is a
              deliberate build, not a retrofit: each one needs its own check, and a
              confident WRONG tick is what we are removing.

              The Stripe card below is untouched: stripeConnected is a real lookup. */}
          {/* Accept payments — same card pattern as the customer CTA below. Deliberately
              skippable: Stripe onboarding is a third-party flow that leaves the app, and
              forcing it would strand anyone who is not ready to hand over bank details. */}
          <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <CreditCardIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Get Paid Online</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Let customers pay invoices by card from the portal</p>
              </div>
            </div>

            {/* SMBMate runs through the TradesMatePro Stripe platform account, so Stripe's
                page is headed with a company this contractor has never heard of, right
                when it asks for bank details. Settings said so; this step -- which
                redirects to exactly the same place -- did not. Renders nothing on
                TradesMatePro. */}
            <SmbMateProcessorNotice className="mb-4" />

            {/* Tell the user what came back from Stripe. Without this, returning is
                indistinguishable from never having gone. */}
            {stripeReturn === 'refresh' && !stripeConnected && (
              <div className="mb-4 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-200">
                Stripe still needs a few details before card payments can go live, or the
                setup link expired. Picking up where you left off will open a fresh one.
              </div>
            )}
            {stripeReturn === 'success' && !stripeConnected && (
              <div className="mb-4 rounded-lg border border-blue-300/60 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-700/60 dark:bg-blue-950/30 dark:text-blue-200">
                Stripe has your details. Card payments switch on once Stripe finishes
                verifying, which is usually quick — you can carry on in the meantime.
              </div>
            )}

            <div className="text-center">
              {stripeConnected ? (
                <div className="inline-flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
                  <CheckCircleIcon className="w-5 h-5" />
                  Card payments are set up
                </div>
              ) : (
                <>
                  <button
                    onClick={connectStripe}
                    disabled={connectingStripe || stripeConnected === null}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <CreditCardIcon className="w-5 h-5" />
                    {/* After a refresh return this is a RESUME, not a fresh start. Same handler either way -- it mints a new account link -- but the label should not ask somebody to "set up" what they just spent five minutes setting up. */}
                    {connectingStripe
                      ? 'Opening Stripe…'
                      : stripeReturn === 'refresh' ? 'Resume Stripe setup' : 'Set Up Card Payments'}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    Takes a few minutes with Stripe. You can do this later in Settings.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Add First Customer - Using Existing Modal */}
          <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Add Your First Customer</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This customer will be ready for your first quote</p>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowAddCustomerModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-pink-600 text-white font-medium rounded-md hover:bg-pink-700"
              >
                <PlusIcon className="w-5 h-5" />
                Add First Customer
              </button>
              <p className="text-sm text-gray-500 mt-2">
                Opens the standard customer creation form
              </p>
            </div>
          </div>

          {/* Skip Option */}
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Prefer to Skip?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You can add your first customer later from the Customers page. 
              You'll still be able to create quotes right away.
            </p>
            {/* Was "Skip & Go to Dashboard →", which was wrong twice: it did not
                navigate anywhere, and it was not the last step — it set setupComplete so
                the Launch button below would enable, leaving another click to make.
                Now that Launch is never disabled, this link's only job is to dismiss the
                add-a-customer prompt, so it says that and promises nothing about a
                destination. Launch is what leaves. */}
            <button
              onClick={skipCustomer}
              className="text-pink-600 hover:text-pink-800 font-medium"
            >
              Skip for now
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="text-lg font-medium text-green-900">Setup Complete!</h3>
                <p className="text-green-700">Your {brand?.name || 'TradesMate'} system is ready to use.</p>
              </div>
            </div>

            {customerCreated && (
              <div className="mt-4 p-4 bg-white rounded-md border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <UserIcon className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-gray-900 dark:text-white">First Customer Added:</h4>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-7">
                  <li>✓ {customerCreated.name || customerCreated.first_name || 'Customer'}</li>
                  {/* Board #2851. This was `{customerCreated.email || 'Email on file'}`, so a
                      customer saved WITHOUT an email fell through to the literal words
                      "Email on file" — next to a tick. The fallback was written as a label
                      and read as a claim, and it claimed the opposite of what happened.
                      Verified live: the created customer's email was NULL while this line
                      said it was on file. Say nothing rather than something untrue. */}
                  {customerCreated.email
                    ? <li>✓ {customerCreated.email}</li>
                    : <li className="text-gray-500">No email added — you can add one later</li>}
                  <li>✓ Ready for your first quote!</li>
                </ul>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">What's Next?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-medium text-sm">1</div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Create Your First Quote</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Go to Quotes and create a quote for {customerCreated ? (customerCreated.name || customerCreated.first_name || 'your customer') : 'your customer'}.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-medium text-sm">2</div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Explore Your Dashboard</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Check out work orders, calendar, and business metrics.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-medium text-sm">3</div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Add More Customers</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Add more customers anytime from the Customers page.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg p-6 text-white text-center">
            <RocketLaunchIcon className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h3 className="text-xl font-bold mb-2">Welcome to <BrandName />!</h3>
            <p className="mb-4 opacity-90">
              You're now ready to manage your field service business like a pro.
            </p>
          </div>
        </>
      )}

      {/* Complete Button */}
      <div className="text-center mt-8">
        {/* NEVER DISABLED. This was `disabled={!setupComplete}` with the label
            "Complete Setup First", which contradicted the screen it sits on three ways:
            the step reports `valid: true` ("Always valid - this is the final step"), the
            panel below says "You can add your first customer later", and adding a customer
            is described as optional throughout. The exit from onboarding was gated on an
            action the page itself calls optional.

            It also gave the user nothing to act on: "Complete Setup First" never said WHAT
            was incomplete, and the only way to unlock it was a text link in a secondary
            panel further down the page. A disabled primary CTA with no stated reason is
            the pattern Nielsen Norman and GOV.UK both advise against, and it is why the
            "Skip" link had to exist at all — it was a key for a lock that should not have
            been fitted.

            Adding a first customer stays the recommended path and still swaps this screen
            to the welcome state; it simply no longer holds the door shut. */}
        {/* NO LAUNCH BUTTON HERE. The wizard footer already shows "Complete Setup" on
            this step -- it is the last step of the flow -- and it runs the same
            completion. Two primary actions worded differently ("Launch SMBMate" and
            "Complete Setup") asked which one finishes setup when either does. This is
            the same duplication removed from Company Basics and Pricing; Go Live was
            missed because its button was worded as a destination rather than a save. */}
      </div>

      {/* Same form as pages/Customers.js — type radios, first/last name split,
          billing address, tags, portal invite. Records created here share the
          same shape and persistence path (createOrLinkCustomer) as customers
          added from the main customer page. */}
      <AddCustomerModal
        open={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCreated={handleCustomerCreated}
      />
    </div>
  );
};

export default GoLiveStep;
